/**
 * Tag Domain Shared Types
 *
 * Task: T-P5-D8-F1-2
 *
 * Prisma-agnostic type contracts for the tags domain.
 * Used by the API service layer, webapp frontend, and worker.
 */

// ============================================================================
// Tag Color Tokens
// ============================================================================

/** Admin-selectable color tokens — semantic names, not hex values */
export const TAG_COLORS = ['purple', 'orange', 'blue', 'green', 'pink', 'fuchsia', 'red', 'gold', 'indigo'] as const;
export type TagColor = (typeof TAG_COLORS)[number];

/** System-reserved colors (not available for admin-created tags) */
export const SYSTEM_TAG_COLORS = ['teal', 'amber'] as const;
export type SystemTagColor = (typeof SYSTEM_TAG_COLORS)[number];

/** Token → hex mapping for UI rendering */
export const TAG_COLOR_HEX_MAP: Record<TagColor | SystemTagColor, string> = {
  purple: '#A78BFA',
  orange: '#F06A50',
  blue: '#60A5FA',
  green: '#6B9E78',
  pink: '#F472B6',
  fuchsia: '#E879F9',
  red: '#EF4444',
  gold: '#FBBF24',
  indigo: '#818CF8',
  teal: '#2DD4BF',
  amber: '#D97706',
};

// ============================================================================
// Assignment Source & Rule Mode Types
// ============================================================================

/** How a client received a tag (mirrors Prisma TagAssignmentSource enum) */
export const TAG_ASSIGNMENT_SOURCES = ['ADMIN', 'COMPLETION', 'FORM'] as const;
export type TagAssignmentSourceType = (typeof TAG_ASSIGNMENT_SOURCES)[number];

/** Offering tag requirement mode (mirrors Prisma TagRuleMode enum) */
export const TAG_RULE_MODES = ['ANY', 'ALL'] as const;
export type TagRuleModeType = (typeof TAG_RULE_MODES)[number];

// ============================================================================
// F2: Intent, Derived Roles, Offering Link Types
// ============================================================================

/** Intent determines what links are required at tag creation time (request param, not stored) */
export const TAG_INTENTS = ['organize', 'unlock', 'award', 'combo'] as const;
export type TagIntent = (typeof TAG_INTENTS)[number];

/** Derived roles computed from tag relationships (not stored, computed via SQL) */
export const TAG_DERIVED_ROLES = ['admin-assigned', 'awarded-on-completion', 'unlocks-bookings', 'progression-key', 'progression-related', 'label-only'] as const;
export type TagDerivedRole = (typeof TAG_DERIVED_ROLES)[number];

/** Offering target shape — determines which persistence layer to use */
export const OFFERING_TARGET_SHAPES = ['session', 'eventTemplate', 'eventInstance'] as const;
export type OfferingTargetShape = (typeof OFFERING_TARGET_SHAPES)[number];

/** Link type — required (unlock) or award (completion) */
export const OFFERING_LINK_TYPES = ['required', 'award'] as const;
export type OfferingLinkType = (typeof OFFERING_LINK_TYPES)[number];

/** Warning severity for tag health issues */
export const WARNING_SEVERITIES = ['light', 'strong'] as const;
export type WarningSeverity = (typeof WARNING_SEVERITIES)[number];

/** Actions available when deleting a tag */
export const TAG_DELETE_ACTIONS = ['remove-links', 'draft-offerings'] as const;
export type TagDeleteAction = (typeof TAG_DELETE_ACTIONS)[number];

// ============================================================================
// F3: Client Tag Assignment DTO Types
// ============================================================================

/** Per-client tag read model — GET /api/admin/clients/:id/tags */
export interface ClientTagDetailDto {
  id: string;
  name: string;
  color: TagColor;
  description: string | null;
  source: TagAssignmentSourceType;
  assignedByName: string | null;
  awardingOfferingName: string | null;
  awardingOfferingType: 'session' | 'event' | null;
  awardingFormName: string | null;
  awardingFormSubmissionId: string | null;
  progressionPathId: string | null;
  progressionPathName: string | null;
  /**
   * Every offering this tag gates, ordered by name. `count === offerings.length`.
   * Null when the tag gates nothing.
   *
   * NOTE: distinct from `TagListItemDto.unlockSummary` and
   * `AttendanceTagAwardSummary.newAwards[].unlockSummary`, which both keep the
   * `{ count, firstOfferingName }` shape.
   */
  unlockSummary: {
    count: number;
    offerings: Array<{ name: string; type: 'session' | 'event' }>;
  } | null;
  createdAt: string;
}

/** Per-tag client read model — GET /api/admin/tags/:id/clients */
export interface TagClientListItemDto {
  id: string;
  firstName: string | null;
  lastName: string | null;
  username: string | null;
  avatarUrl: string | null;
  source: TagAssignmentSourceType;
  awardingOfferingName: string | null;
  awardingOfferingType: 'session' | 'event' | null;
  awardingFormName: string | null;
  assignedAt: string;
}

/** Single offering in remove-impact preview */
export interface RemoveImpactOfferingDto {
  targetId: string;
  targetShape: OfferingTargetShape;
  name: string;
  offeringType: 'session' | 'event';
  instanceCount: number | null;
  templateName: string | null;
}

/** Remove-impact preview response — GET /api/admin/clients/:id/tags/:tagId/impact */
export interface RemoveImpactDto {
  tagId: string;
  tagName: string;
  tagColor: TagColor;
  affectedOfferings: RemoveImpactOfferingDto[];
  progressionImpact: { pathId: string; pathName: string } | null;
  isLabelOnly: boolean;
}

/** Bulk operation result — used by all bulk assign/remove endpoints */
export interface BulkTagResultDto {
  affected: number;
  skipped: number;
  total: number;
}

/** Common tag in intersection — POST /api/admin/clients/bulk/tags/common */
export interface CommonTagDto {
  id: string;
  name: string;
  color: TagColor;
  description: string | null;
  holderCount: number;
}

// ============================================================================
// F5: Attendance-Driven Tag Award Types
// ============================================================================

import type { z } from 'zod';
import type { tagAwardResultSchema, attendanceTagAwardSummarySchema } from './schemas.js';

/** Per-attendee tag award result — returned by IClientTagAwardWriter */
export type TagAwardResult = z.infer<typeof tagAwardResultSchema>;

/** Aggregate award summary across all attendees in a batch */
export type AttendanceTagAwardSummary = z.infer<typeof attendanceTagAwardSummarySchema>;
